import React, { useState, useEffect } from 'react';
import { Modal, useAuth } from '@nuance/ui';
import { User, Mail, Shield } from 'lucide-react';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';

interface UserSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const TIMEZONE_OPTIONS = [
  { value: 'auto', label: 'Auto (Browser Default)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
] as const;

type ThemeValue = 'light' | 'dark' | 'system';

function getStoredTheme(): ThemeValue {
  return (localStorage.getItem('nuance-theme') as ThemeValue) ?? 'system';
}

function applyTheme(theme: ThemeValue) {
  localStorage.setItem('nuance-theme', theme);
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark);
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export function UserSettingsModal({ open, onClose }: UserSettingsModalProps) {
  const { user } = useAuth();
  const { preferences, updatePreferences, effectiveTimezone } = useUserPreferences();
  const [theme, setTheme] = useState<ThemeValue>(getStoredTheme);
  const [now, setNow] = useState(new Date());

  // Re-read theme each time modal opens
  useEffect(() => {
    if (open) setTheme(getStoredTheme());
  }, [open]);

  // Live clock
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [open]);

  const hour12 = preferences.dateFormat === '12h';

  const handleThemeChange = (value: ThemeValue) => {
    setTheme(value);
    applyTheme(value);
  };

  const previewTime =
    now.toLocaleDateString(undefined, { timeZone: effectiveTimezone }) +
    ' ' +
    now.toLocaleTimeString(undefined, {
      timeZone: effectiveTimezone,
      hour12,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="User Settings"
      size="sm"
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          Save
        </button>
      }
    >
      <div className="space-y-6">
        {/* Profile */}
        <div className="flex items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-semibold text-blue-600">
              {user?.name?.split(' ').map((n) => n[0]).join('') ?? 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {user?.name ?? 'User'}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Mail className="w-3 h-3 text-gray-400" />
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {user?.email ?? 'Not signed in'}
              </p>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Shield className="w-3 h-3 text-gray-400" />
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {user?.roles.includes('admin') ? 'Administrator' : user?.roles.includes('client') ? 'Client User' : 'User'}
              </p>
            </div>
          </div>
        </div>

        {/* Timezone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Timezone
          </label>
          <select
            value={preferences.timezone}
            onChange={(e) => updatePreferences({ timezone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        {/* Time Format */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Time Format
          </label>
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => updatePreferences({ dateFormat: '12h' })}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                preferences.dateFormat === '12h'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              12-hour
            </button>
            <button
              onClick={() => updatePreferences({ dateFormat: '24h' })}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                preferences.dateFormat === '24h'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              24-hour
            </button>
          </div>
        </div>

        {/* Theme */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Theme
          </label>
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {(['light', 'dark', 'system'] as const).map((val) => (
              <button
                key={val}
                onClick={() => handleThemeChange(val)}
                className={`flex-1 px-4 py-2 text-sm font-medium capitalize transition-colors ${
                  theme === val
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {val}
              </button>
            ))}
          </div>
        </div>

        {/* Live Preview */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
            Current Time Preview
          </p>
          <p className="text-lg font-mono text-gray-900 dark:text-white">
            {previewTime}
          </p>
        </div>
      </div>
    </Modal>
  );
}
